import { useEffect, useId, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { WeighResult, WeighTask } from '@popcart/core/v1';
import { api, errorMessage, errorStatus } from '../../api';
import { StatusPill } from '../../ui/bits';
import { Fig } from '../../ui/viz';
import { Icon } from '../../ui/Icon';
import { ProductArt } from '../../catalog/art';
import { clock, peso } from '../../lib/format.js';
import { gramsLabel, parseGrams, perKg, photoProblem, recalc, savedMessage } from './lib/weigh.js';
import { preparePhoto } from './photo';

/**
 * Weigh one Fresh line: a big gram field inside the Night Ink scale readout with live
 * recalculation (shared finalLine maths), the "this is your fish" photo, then Save weight.
 * The server's WeighResult is what we show once saved.
 */
export function WeighPanel({
  shopId,
  task,
  lastResult,
  locked,
  primary,
  onSaved,
  onConflict,
}: {
  shopId: string;
  task: WeighTask;
  lastResult: WeighResult | null;
  /** The order has moved on (ready or later): no more weighing. */
  locked: boolean;
  /** Save weight is the screen's Volt only until the order's "Ready for rider" takes over. */
  primary: boolean;
  onSaved(t: WeighTask, r: WeighResult, message: string): void;
  onConflict(): void;
}) {
  const ids = useId();
  const gramsId = `${ids}-grams`;
  const gramsHelp = `${ids}-grams-help`;
  const gramsErr = `${ids}-grams-err`;
  const photoErrId = `${ids}-photo-err`;
  const [raw, setRaw] = useState('');
  const [touched, setTouched] = useState(false);
  const [photo, setPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [photoErr, setPhotoErr] = useState<string | null>(null);
  const [skipOpen, setSkipOpen] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const photoInput = useRef<HTMLInputElement>(null);

  const parsed = parseGrams(raw);
  const live = parsed.ok ? recalc(task, parsed.grams) : null;
  const showGramsErr = !parsed.ok && !parsed.empty && touched ? parsed.error : null;
  const weighed = task.weighed;

  // Autofocus the gram field for lines still to weigh (a re-weigh is a deliberate tap).
  useEffect(() => {
    if (!weighed && !locked) input.current?.focus();
    // Only on open (the panel is keyed by line): a later poll must not steal focus.
  }, []);

  useEffect(() => {
    if (!photo) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(photo);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  const save = useMutation({
    mutationFn: async ({ grams, file }: { grams: number; file: File | null }) => {
      const form = new FormData();
      form.append('grams', String(grams));
      if (file) form.append('photo', await preparePhoto(file), 'weigh.jpg');
      return api.weighItem(shopId, task.orderItemId, form);
    },
    onSuccess: (r) => {
      setFormErr(null);
      setRaw('');
      setTouched(false);
      setPhoto(null);
      onSaved(task, r, savedMessage(task, r));
    },
    onError: (err) => {
      if (errorStatus(err) === 409) {
        setFormErr(errorMessage(err, 'This order has moved on and can’t be weighed again.'));
        onConflict();
      } else if (err instanceof Error && !('status' in err)) {
        setFormErr(err.message);
      } else {
        setFormErr(errorMessage(err, "Couldn't save the weight. Try again."));
      }
    },
  });

  const submit = (skipPhoto: boolean) => {
    setTouched(true);
    setFormErr(null);
    if (!parsed.ok) {
      setFormErr(parsed.error);
      input.current?.focus();
      return;
    }
    if (!photo && !skipPhoto) {
      setPhotoErr('Take the “this is your fish” photo, or choose Skip photo.');
      photoInput.current?.focus();
      return;
    }
    setSkipOpen(false);
    save.mutate({ grams: parsed.grams, file: skipPhoto ? null : photo });
  };

  // Figures lead; the words ride as small labels.
  const facts: [string, string, string?][] = [
    ['Estimate', gramsLabel(task.estGramsTotal), `${task.qty} × ${task.sizeLabel}`],
    ['Est. price', peso(task.estLineCentavos)],
    ['Per kg', peso(task.pricePerKgCentavos), perKg(task.pricePerKgCentavos)],
    ['Tolerance', `+${task.tolerancePct}%`, `+${task.tolerancePct}% max`],
  ];

  return (
    <form
      className="stack s12"
      aria-labelledby={`${ids}-h`}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (!locked && !save.isPending) submit(false);
      }}
    >
      <div className="fw-item-h">
        <ProductArt className="fw-item-art" name={task.productName} line="fresh" />
        <div className="stack s4" style={{ minWidth: 0 }}>
          <h3 id={`${ids}-h`}>
            {task.productName}
            {task.prepLabel ? `, ${task.prepLabel.toLowerCase()}` : ''}
          </h3>
          <span className="dv-sub">{task.qty} × {task.sizeLabel}</span>
        </div>
        <span className={weighed ? 'dv-chip good' : 'dv-chip'}>{weighed ? 'Weighed' : 'Weigh'}</span>
      </div>
      <div className="fw-facts">
        {facts.map(([k, v, tip]) => (
          <div key={k} title={tip}>
            <span className="dv-label">{k}</span>
            <Fig text={v} size="sm" animate={false} />
          </div>
        ))}
      </div>

      {weighed ? (
        <div className="fw-result">
          <ProductArt className="fw-thumb" src={weighed.photoUrl} name={task.productName} line="fresh" alt={weighed.photoUrl ? `Photo of the ${task.productName} as weighed` : ''} />
          <div className="stack s4">
            <span className="dv-label">Saved {clock(weighed.weighedAt)}</span>
            <span className="row wrap" style={{ gap: 'var(--pc-space-16)' }}>
              <Fig text={gramsLabel(weighed.actualGrams)} size="md" animate={false} />
              <Fig text={peso(weighed.finalLineCentavos)} size="md" animate={false} />
            </span>
            <span className="dv-sub">
              {weighed.deltaCentavos < 0
                ? `${peso(-weighed.deltaCentavos)} less than estimate`
                : weighed.deltaCentavos > 0
                  ? `${peso(weighed.deltaCentavos)} more than estimate`
                  : 'Same as the estimate'}
              {lastResult && lastResult.shopAbsorbedCentavos > 0 ? ` · shop absorbs ${peso(lastResult.shopAbsorbedCentavos)}` : ''}
              {weighed.photoUrl ? '' : ' · no photo'}
            </span>
            {lastResult?.allWeighed ? <SettlementLine r={lastResult} /> : null}
          </div>
        </div>
      ) : null}

      {locked ? (
        <p className="dv-quiet">
          <Icon name="check" />
          Weights are final
        </p>
      ) : (
        <>
          <div className="fw-weigh">
            <div className="fw-scale">
              <span className="fw-scale-top" aria-hidden="true">
                <span className="dv-pulse" />
                {weighed ? 'Re-weigh' : 'Scale'}
              </span>
              <label htmlFor={gramsId}>{weighed ? 'New reading in grams' : 'Scale reading in grams'}</label>
              <div className="fw-grams">
                <input
                  ref={input}
                  id={gramsId}
                  name="grams"
                  inputMode="numeric"
                  autoComplete="off"
                  enterKeyHint="done"
                  placeholder="0"
                  value={raw}
                  aria-invalid={showGramsErr ? true : undefined}
                  aria-describedby={showGramsErr ? `${gramsErr} ${gramsHelp}` : gramsHelp}
                  onChange={(e) => {
                    setRaw(e.target.value);
                    setFormErr(null);
                  }}
                  onBlur={() => setTouched(true)}
                />
                <span className="unit" aria-hidden="true">
                  g
                </span>
              </div>
              {showGramsErr ? (
                <span id={gramsErr} className="pill st-error">
                  {showGramsErr}
                </span>
              ) : null}
              {live ? (
                <>
                  <span className="fw-final">
                    <span className="sr-only">{live.finalText}</span>
                    <span aria-hidden="true" className="sub">Final</span>
                    <Fig text={peso(live.finalCentavos)} size="lg" animate={false} />
                  </span>
                  <StatusPill tone={live.tone}>{live.pill}</StatusPill>
                  {live.capped ? <span className="t-caption sub">{live.deltaText} at the scale; the customer pays at most {peso(live.capCentavos)}.</span> : null}
                  {live.check ? (
                    <StatusPill tone="warning">
                      <Icon name="alert" size="sm" />
                      {live.check}
                    </StatusPill>
                  ) : null}
                </>
              ) : (
                <span className="t-caption sub" title="Type the reading to see the final price.">
                  Est. {peso(task.estLineCentavos)} · {gramsLabel(task.estGramsTotal)}
                </span>
              )}
              <span id={gramsHelp} className="t-caption sub">
                Whole grams · Enter saves
              </span>
            </div>

            <div className="stack s4">
              <label className={`fw-photo${preview ? ' has' : ''}`} data-invalid={photoErr ? 'true' : undefined}>
                <input
                  ref={photoInput}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  aria-describedby={photoErr ? photoErrId : undefined}
                  aria-invalid={photoErr ? true : undefined}
                  onChange={(e) => {
                    const f = e.target.files?.[0] ?? null;
                    e.target.value = '';
                    if (!f) return;
                    const p = photoProblem(f);
                    setPhotoErr(p);
                    if (!p) setPhoto(f);
                  }}
                />
                {preview ? (
                  <img src={preview} alt={`Photo of the ${task.productName} to send the customer`} />
                ) : (
                  <span className="stack s4" style={{ alignItems: 'center' }}>
                    <ProductArt className="fw-photo-art" name={task.productName} line="fresh" />
                    <span className="row t-label" title="The customer sees it with the final price.">
                      <Icon name="camera" />
                      Take the “this is your fish” photo
                    </span>
                  </span>
                )}
                {preview ? <span className="sr-only">Photo added. Choose to retake.</span> : null}
              </label>
              {photoErr ? (
                <span id={photoErrId} className="pill st-error err-pill">
                  {photoErr}
                </span>
              ) : null}
              <div className="row wrap">
                {photo ? (
                  <button type="button" className="btn btn-quiet" onClick={() => setPhoto(null)}>
                    <Icon name="close" size="sm" />
                    Remove photo
                  </button>
                ) : (
                  <button type="button" className="btn btn-quiet" onClick={() => setSkipOpen(true)}>
                    Skip photo
                  </button>
                )}
              </div>
            </div>
          </div>

          {formErr ? (
            <div className="status-msg st-error" role="alert">
              {formErr}
            </div>
          ) : null}

          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button type="submit" className={primary ? 'btn btn-primary btn-lg' : 'btn btn-lg'} disabled={save.isPending} aria-busy={save.isPending || undefined}>
              <Icon name="scale" />
              {save.isPending ? 'Saving…' : weighed ? 'Save new weight' : 'Save weight'}
            </button>
          </div>
        </>
      )}

      <SkipPhotoDialog
        open={skipOpen}
        productName={task.productName}
        busy={save.isPending}
        onConfirm={() => submit(true)}
        onClose={() => setSkipOpen(false)}
        onTakePhoto={() => {
          setSkipOpen(false);
          photoInput.current?.click();
        }}
      />
    </form>
  );
}

function SettlementLine({ r }: { r: WeighResult }) {
  const parts = ['Every Fresh item weighed'];
  if (r.orderFinalTotalCentavos !== null) parts.push(`order total ${peso(r.orderFinalTotalCentavos)}`);
  if (r.codDueCentavos !== null) parts.push(`COD due is now ${peso(r.codDueCentavos)}`);
  else if (r.refundCentavos) parts.push(`${peso(r.refundCentavos)} goes back to the customer`);
  return <span className="t-caption">{parts.join(' · ')}</span>;
}

function SkipPhotoDialog({
  open,
  productName,
  busy,
  onConfirm,
  onClose,
  onTakePhoto,
}: {
  open: boolean;
  productName: string;
  busy: boolean;
  onConfirm(): void;
  onClose(): void;
  onTakePhoto(): void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    else if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog ref={ref} className="fw-dialog" aria-labelledby={`${id}-t`} aria-describedby={`${id}-b`} onClose={onClose}>
      <div className="stack s24">
        <h2 id={`${id}-t`} className="t-headline">
          Save without a photo?
        </h2>
        <p id={`${id}-b`} className="t-body">
          The customer won't see their {productName.toLowerCase()} before it arrives. Only skip when the camera isn't working.
        </p>
        <div className="row wrap" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn" onClick={onTakePhoto}>
            <Icon name="camera" size="sm" />
            Take the photo
          </button>
          <button type="button" className="btn" disabled={busy} aria-busy={busy || undefined} onClick={onConfirm}>
            Save without photo
          </button>
        </div>
      </div>
    </dialog>
  );
}
